import { OverflowMenu, Tooltip } from "@carbon/react";
import { ServerError } from "@flaminem/utils";
import { FC, useState } from "react";

import { AuthorizedMenu, AuthorizedOverflowMenuItem } from "src/authentication/AuthorizedMenu";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider";
import { ModalTransition } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer";
import { useEnableBlacklistElementMutation } from "src/graphql/anti-fraud/queries/blacklists/mutations/EnableBlacklistElementMutation";
import { useT } from "src/internationalization";
import { BlacklistElement } from "src/types/blacklist";

import { useEditableProperties } from "../useEditableProperties";

import { BlacklistElementModal } from "./BlacklistElementModal";
import { ConfirmDeactivateBlacklistModal } from "./ConfirmDeactivateBlacklistModal";

export type BlacklistActionsProps = {
  blacklistElementRecord: BlacklistElement;
  onStatusChange: () => void;
  onElementUpdated: () => void;
};

export const BlacklistActions: FC<BlacklistActionsProps> = ({
  blacklistElementRecord,
  onStatusChange,
  onElementUpdated,
}) => {
  const t = useT();
  const { editProperties, disable: disableEdit } = useEditableProperties();

  const { showErrorToast, showSuccessToast } = useToast();
  const [blacklistToDisable, setBlacklistToDisable] = useState<BlacklistElement | null>(null);
  const [blacklistToUpdate, setBlacklistToUpdate] = useState<BlacklistElement | null>(null);

  const { performMutation: enableBlacklist } = useEnableBlacklistElementMutation({
    onSuccess: () => {
      showSuccessToast(
        t("page.blacklists.table.action.update.status.success.message", {
          status: t("page.blacklists.table.status.inactive").toLowerCase(),
        })
      );

      onStatusChange();
    },
    onError: (e: ServerError) => {
      showErrorToast(e.message);
    },
  });

  return (
    <div
      role="presentation"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div>
        <AuthorizedMenu
          menuItemsPermissions={[
            { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.DISABLE] },
            { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.EDIT] },
          ]}
        >
          <OverflowMenu flipped={true} menuOptionsClass="!w-[100px]">
            <AuthorizedOverflowMenuItem
              requiredPermissions={[
                { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.DISABLE] },
              ]}
              itemText={
                <div>
                  {blacklistElementRecord.status
                    ? t("page.blacklists.table.menu.disable")
                    : t("page.blacklists.table.menu.activate")}
                </div>
              }
              onClick={() => {
                if (blacklistElementRecord.status)
                  return setBlacklistToDisable(blacklistElementRecord);
                return enableBlacklist({ id: blacklistElementRecord.id });
              }}
            />
            {disableEdit
              ? blacklistElementRecord.status && (
                  <Tooltip label={t("page.blacklists.table.button.disable")}>
                    <span>
                      <AuthorizedOverflowMenuItem
                        requiredPermissions={[
                          { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.EDIT] },
                        ]}
                        itemText={<div>{t("general.actions.edit")}</div>}
                        disabled={disableEdit}
                        onClick={() => {
                          setBlacklistToUpdate(blacklistElementRecord);
                        }}
                      />
                    </span>
                  </Tooltip>
                )
              : blacklistElementRecord.status && (
                  <AuthorizedOverflowMenuItem
                    requiredPermissions={[
                      { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.EDIT] },
                    ]}
                    itemText={<div>{t("general.actions.edit")}</div>}
                    disabled={disableEdit}
                    onClick={() => {
                      setBlacklistToUpdate(blacklistElementRecord);
                    }}
                  />
                )}
          </OverflowMenu>
        </AuthorizedMenu>
      </div>
      <ModalTransition>
        {blacklistToDisable && (
          <ConfirmDeactivateBlacklistModal
            blacklistElement={blacklistToDisable}
            onClose={() => setBlacklistToDisable(null)}
            onConfirm={() => {
              onStatusChange();
              setBlacklistToDisable(null);
            }}
          />
        )}
        {blacklistToUpdate && (
          <BlacklistElementModal
            onClose={() => setBlacklistToUpdate(null)}
            onSuccess={() => {
              setBlacklistToUpdate(null);
              onElementUpdated();
            }}
            item={{
              properties: blacklistToUpdate.properties.map((p) => ({
                property: p.property,
                value: p.value.value.toString(),
              })),
              id: blacklistToUpdate?.id,
              sourceProperties: blacklistToDisable?.sourceProperties ?? [],
            }}
            editProperties={editProperties}
          />
        )}
      </ModalTransition>
    </div>
  );
};
